import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Zap } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Earned-so-far XP for the walk. PRESENTATION ONLY — derived from how many
 * decisions the learner has already made, never stored, never scored.
 *
 * The number rolls up on change and a small "+N" floats off it, so progress
 * feels earned instead of measured.
 */
export function XpPill({
  xp, gained, className,
}: { xp: number; gained: number | null; className?: string }) {
  const reduce = useReducedMotion();

  return (
    <div
      className={cn(
        "relative inline-flex items-center gap-1.5 rounded-full border border-celebrate/30",
        "bg-celebrate/10 px-3 py-1",
        className,
      )}
    >
      <Zap className="h-3.5 w-3.5 text-celebrate" aria-hidden />
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={xp}
          initial={reduce ? undefined : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? undefined : { opacity: 0, y: -6 }}
          transition={{ duration: 0.22 }}
          className="text-xs font-medium tabular-nums text-celebrate"
        >
          {xp} XP
        </motion.span>
      </AnimatePresence>

      {/* The celebration degrades, it does not disappear: with reduced motion
          the "+N" is still shown in place, just without the float. */}
      <AnimatePresence>
        {gained !== null && (
          reduce ? (
            <span
              key={`gain-static-${xp}`}
              className="pointer-events-none absolute -top-4 right-1 text-xs font-semibold text-celebrate"
              aria-hidden
            >
              +{gained}
            </span>
          ) : (
            <motion.span
              key={`gain-${xp}`}
              initial={{ opacity: 0, y: 0, scale: 0.9 }}
              animate={{ opacity: 1, y: -22, scale: 1 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="pointer-events-none absolute -top-1 right-1 text-xs font-semibold text-celebrate"
              aria-hidden
            >
              +{gained}
            </motion.span>
          )
        )}
      </AnimatePresence>

      <span className="sr-only" aria-live="polite">
        {xp} experience points earned so far
      </span>
    </div>
  );
}
